import { BookOpen, Check, Heart, Sparkles, Sprout } from "lucide-react";
import { Bar } from "@/components/ResultView";

const DISC_LABEL = { D: "Dominance", I: "Influence", S: "Steadiness", C: "Conscientiousness" };

export const SampleTag = ({ children = "Fictional sample data" }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-teal bg-teal-soft rounded-full px-3 py-1" data-testid="sample-tag">
    <span className="h-1.5 w-1.5 rounded-full bg-teal" />{children}
  </span>
);

const Heading = ({ eyebrow, title, sub, light }) => (
  <div>
    {eyebrow && <p className={`eyebrow ${light ? "!text-[#8FD3C6]" : ""}`}>{eyebrow}</p>}
    <h2 className={`font-display text-3xl md:text-4xl font-semibold tracking-tight mt-3 ${light ? "text-white" : "text-navy"}`}>{title}</h2>
    {sub && <p className={`mt-3 text-base md:text-lg ${light ? "text-white/75" : "text-muted-ink"}`}>{sub}</p>}
  </div>
);

const Ticks = ({ items }) => (
  <ul className="space-y-2.5 mt-4">
    {items.map((x) => <li key={x} className="flex gap-2 text-sm text-muted-ink leading-relaxed"><Check size={16} className="text-teal shrink-0 mt-0.5" />{x}</li>)}
  </ul>
);

export const Overview = ({ v }) => {
  const stats = [["Primary DISC Personality", "Influence"], ["Secondary DISC Personality", "Steadiness"], ["Top Spiritual Gift", "Hospitality"], ["Top Ministry Match", v.matches[0].name]];
  return (
    <section className="card-soft p-6 sm:p-8 md:p-10" data-testid="sample-overview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="eyebrow">Sample report · {v.name}</p>
        <SampleTag />
      </div>
      <h2 className="font-display text-navy text-3xl md:text-5xl font-semibold tracking-tight mt-4">Jordan, Here's How You're <span className="font-accent text-teal">Wired.</span></h2>
      <p className="text-muted-ink text-base md:text-lg leading-relaxed mt-5 max-w-3xl" data-testid="sample-summary">{v.summary}</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-8">
        {stats.map(([k, val], i) => (
          <div key={k} className={`rounded-2xl p-4 sm:p-5 lift ${i === 3 ? "bg-navy text-white" : "bg-paper"}`} data-testid={`sample-stat-${i}`}>
            <p className={`text-[11px] uppercase tracking-[0.12em] ${i === 3 ? "text-[#8FD3C6]" : "text-muted-ink"}`}>{k}</p>
            <p className={`font-display text-lg sm:text-xl font-semibold mt-2 ${i === 3 ? "" : "text-navy"}`}>{val}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export const DiscProfile = ({ v }) => (
  <section className="card-soft p-6 sm:p-8 md:p-10" data-testid="sample-disc">
    <Heading eyebrow="DISC personality profile" title={<>Influence <span className="font-accent text-teal">+</span> Steadiness</>} sub="How Jordan naturally communicates, works with others and responds to their environment." />
    <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 mt-8">
      <div className="lg:col-span-2 space-y-5">
        {Object.entries(v.disc_scores).map(([k, s]) => (
          <div key={k} data-testid={`sample-disc-row-${k}`}>
            <div className="flex justify-between text-sm mb-1.5"><span className="text-navy font-medium">{DISC_LABEL[k]} ({k})</span><span className="text-muted-ink font-mono-dt">{s}%</span></div>
            <Bar value={s} testid={`sample-disc-bar-${k}`} />
          </div>
        ))}
        <div className="grid grid-cols-2 gap-3 pt-3">
          <div className="bg-navy text-white rounded-xl p-4" data-testid="sample-disc-primary"><p className="text-[11px] uppercase tracking-[0.12em] text-[#8FD3C6]">Primary</p><p className="font-display text-lg font-semibold mt-1">Influence (I)</p></div>
          <div className="bg-teal-soft rounded-xl p-4" data-testid="sample-disc-secondary"><p className="text-[11px] uppercase tracking-[0.12em] text-teal">Secondary</p><p className="font-display text-navy text-lg font-semibold mt-1">Steadiness (S)</p></div>
        </div>
      </div>
      <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4">
        {v.disc_insights.map(([t, b], i) => (
          <div key={t} className="bg-paper rounded-xl p-5" data-testid={`sample-disc-insight-${i}`}>
            <p className="font-display text-navy font-semibold">{t}</p>
            <p className="text-sm text-muted-ink leading-relaxed mt-2">{b}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const Gifts = ({ v, reference }) => {
  const ref = (k) => reference?.gifts.find((g) => g.key === k);
  return (
    <section className="card-soft p-6 sm:p-8 md:p-10" data-testid="sample-gifts">
      <Heading eyebrow="Spiritual gifts" title="Your Spiritual Gifts" sub="Discover how God has equipped you to serve others." />
      <div className="grid lg:grid-cols-5 gap-8 mt-8">
        <div className="lg:col-span-3 space-y-5">
          {v.gift_scores.map((g, i) => (
            <div key={g.key} data-testid={`sample-gift-${i}`}>
              <div className="flex justify-between text-sm mb-1.5"><span className="text-navy font-medium">{g.name}</span><span className="text-muted-ink font-mono-dt">{g.score}%</span></div>
              <Bar value={g.score} testid={`sample-gift-bar-${i}`} />
              {ref(g.key) && <p className="text-xs text-muted-ink mt-2 leading-relaxed">{ref(g.key).description} <span className="inline-flex items-center gap-1 text-teal font-medium"><BookOpen size={12} />{ref(g.key).scripture}</span></p>}
            </div>
          ))}
        </div>
        <div className="lg:col-span-2 bg-navy text-white rounded-2xl p-6 sm:p-7" data-testid="sample-top-gift">
          <p className="eyebrow !text-[#8FD3C6]">Top spiritual gift</p>
          <p className="font-display text-3xl font-semibold mt-2">Hospitality</p>
          <p className="text-white/80 text-sm leading-relaxed mt-4">{v.hospitality_note}</p>
        </div>
      </div>
    </section>
  );
};

export const Together = ({ v }) => {
  const cols = [[Sparkles, "Your Strengths", v.together.strengths], [Heart, "How You Work Best", v.together.work_best], [Sprout, "Opportunities for Growth", v.together.growth]];
  return (
    <section className="card-soft p-6 sm:p-8 md:p-10" data-testid="sample-together">
      <Heading eyebrow="Personality + gifts" title="How Your Personality and Gifts Work Together" />
      <p className="text-muted-ink leading-relaxed mt-4 max-w-3xl">{v.together.intro}</p>
      <div className="grid md:grid-cols-3 gap-4 mt-8">
        {cols.map(([Icon, t, items], i) => (
          <div key={t} className="bg-paper rounded-xl p-6 lift" data-testid={`sample-together-${i}`}>
            <Icon size={20} className="text-teal" />
            <p className="font-display text-navy text-lg font-semibold mt-3">{t}</p>
            <Ticks items={items} />
          </div>
        ))}
      </div>
    </section>
  );
};

export { Heading, DISC_LABEL };
